import { useTranslation } from 'react-i18next';
import { useStore } from 'react-redux';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import type { RootState } from 'features/app/store/store';
import {
  addPlusToPhoneNumber,
  parsePhoneNumber,
} from 'features/call/call-base/helpers';
import { hearingAudioReady } from 'features/call/call-base/store';
import {
  callIdSelector,
  isHearingToDeafSelector,
} from 'features/call/call-base/store/selectors';
import { deafPhoneNumberByModeSelector } from 'features/call/call-deaf/store';
import { AddHearingStatus } from 'features/call/call-details/enums';
import { setAddHearingStatus } from 'features/call/call-details/store';
import {
  findLatestHearingByPhoneNumber,
  toHearingDialString,
} from 'features/call/call-hearing/helpers';
import { useHearings, useN11Resolver } from 'features/call/call-hearing/hooks';
import { HearingParticipantType } from 'features/call/call-hearing/interfaces';
import {
  addHearing,
  callErrorSelector,
  clearCallError,
  getHearingsSessionState,
  primaryHearingPhoneNumberSelector,
  setCallError,
  setHearingLoadingStateByPhoneNumber,
  setPrimaryHearingPhoneNumber,
} from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { callerSessionAnalyticsError } from 'features/caller-session/helpers';
import { checkShouldAllowNewHearingConnection } from 'features/caller-session/store';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { isSpecialPhoneNumber } from 'features/n11/helpers';
import { handleError } from 'features/notification/store';
import { isTeamingInProgressSelector } from 'features/teaming/teaming-base/store';
import { agentNumberSelector, userNameSelector } from 'features/user/store';
import {
  registerInFlightAddParticipant,
  takeInFlightAddParticipant,
  validateConnectionPreconditions,
  isAbortError,
  isWarningError,
  createBackendProcessingDelay,
} from 'features/voice-meeting/helpers';
import { useVoiceMeetingSessionConnect } from 'features/voice-meeting/hooks';
import type { VoiceSessionHearingParticipant } from 'features/voice-meeting/interfaces';
import { VoiceMeetingService } from 'features/voice-meeting/services';
import { VoiceSessionStatus } from 'features/voice-session/enums';
import {
  resetVoiceSession,
  setVoiceSessionLoading,
  setVoiceSessionStatus,
  setVoiceSessionThunk,
} from 'features/voice-session/store';
import {
  sendAnalyticsInfo,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';
import { BACKEND_DISCONNECT_PROCESSING_DELAY_MS } from 'features/voice-meeting/constants';

// Allow backend service time to process CallDisconnect and clean up session

export const useVoiceMeetingSessionInit = () => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const primaryHearingPhoneNumber = useAppSelector(
    primaryHearingPhoneNumberSelector
  );
  const deafPhoneNumberByMode = useAppSelector(deafPhoneNumberByModeSelector);
  const agentNumber = useAppSelector(agentNumberSelector);
  const agentUserName = useAppSelector(userNameSelector);
  const callId = useAppSelector(callIdSelector);
  const isTeaming = useAppSelector(isTeamingInProgressSelector);
  const isHearingToDeafCall = useAppSelector(isHearingToDeafSelector);
  const { resolve: resolveN11PhoneNumber } = useN11Resolver();

  const { startNewSession, connectUserToExistingSession } =
    useVoiceMeetingSessionConnect();
  useHearings();
  const isVrs = useAppSelector(isVrsModeSelector);
  const { t } = useTranslation('translation', {
    keyPrefix: 'callErrors',
  });
  const callError = useAppSelector(callErrorSelector);

  const replaceN11Number = async (
    n11PhoneNumber: string,
    isSecondary = false
  ) => {
    try {
      const { phoneNumber, n11Status } = await resolveN11PhoneNumber(
        n11PhoneNumber,
        callId
      );

      if (!phoneNumber || n11Status === 1) {
        dispatch(setCallError(t('unableConnectNumber')));
        return n11PhoneNumber;
      }

      if (!isSecondary) {
        dispatch(setPrimaryHearingPhoneNumber(phoneNumber));
      } else {
        dispatch(
          addHearing({
            id: '',
            phoneNumber: addPlusToPhoneNumber(parsePhoneNumber(phoneNumber)),
            name: phoneNumber,
            location: '',
            phoneExtension: '',
            status: 'disconnected',
            type: HearingParticipantType.SECONDARY,
            isLoading: false,
          })
        );
      }
      return phoneNumber;
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'replaceN11Number',
        })
      );
    }
  };

  const clearCallErrorIfExists = () => {
    if (callError) {
      dispatch(clearCallError());
    }
  };

  const createNewVoiceSession = async (phoneNumber: string) => {
    let hearingPhoneNumber = phoneNumber;
    let resolveInFlight:
      | ((value: VoiceSessionHearingParticipant | null) => void)
      | undefined;
    try {
      dispatch(setVoiceSessionLoading(true));
      if (isVrs && isSpecialPhoneNumber(phoneNumber)) {
        const replacedNumber = await replaceN11Number(phoneNumber);
        hearingPhoneNumber = replacedNumber ?? phoneNumber;
        if (phoneNumber === hearingPhoneNumber) {
          return;
        }
      }
      clearCallErrorIfExists();
      dispatch(setVoiceSessionStatus(VoiceSessionStatus.CONNECTING));
      const { shouldAllow, reasonMessage } = dispatch(
        checkShouldAllowNewHearingConnection(phoneNumber)
      );
      if (!shouldAllow) {
        dispatch(setAddHearingStatus(AddHearingStatus.ENTER_PHONE_NUMBER));
        dispatch(callerSessionAnalyticsError(reasonMessage));
        setHearingLoadingStateByPhoneNumber({ isLoading: false, phoneNumber });
        return;
      }

      // Register before $connecting so cancel-before-id can always take the
      // promise (covers createNewVoiceSession, not only add-to-existing).
      const inFlight = new Promise<VoiceSessionHearingParticipant | null>(
        (resolve) => {
          resolveInFlight = resolve;
        }
      );
      const inFlightEntry = registerInFlightAddParticipant(
        hearingPhoneNumber,
        inFlight
      );
      ParticipantsStatusEventBus.hearing.$connecting.next(hearingPhoneNumber);

      const hearingParticipant = await startNewSession({
        agentUserName,
        callId,
        deafPhoneNumber: deafPhoneNumberByMode,
        hearingPhoneNumber: toHearingDialString(hearingPhoneNumber),
      });
      // Stamp before resolve so cancel's late cleanup (already awaiting) sees
      // the session id even if a forced teardown clears Redux/service next.
      inFlightEntry.sessionId =
        VoiceMeetingService.getInstance().getSessionId() || '';
      console.log('HearingDebug: new-session stamped in-flight sessionId', {
        phoneNumber: hearingPhoneNumber,
        sessionId: inFlightEntry.sessionId || null,
        participantId: hearingParticipant?.id ?? null,
      });
      resolveInFlight?.(hearingParticipant);

      const liveHearing = findLatestHearingByPhoneNumber(
        store.getState().callHearing.hearings,
        hearingPhoneNumber
      );
      if (liveHearing?.teardownIntent) {
        console.log(
          'HearingDebug: createNewVoiceSession aborted (teardownIntent set)',
          {
            phoneNumber: hearingPhoneNumber,
            teardownIntent: liveHearing.teardownIntent,
            participantId: hearingParticipant?.id ?? null,
          }
        );
        return;
      }
      takeInFlightAddParticipant(hearingPhoneNumber);
    } catch (error: unknown) {
      resolveInFlight?.(null);
      takeInFlightAddParticipant(hearingPhoneNumber);
      const errorWithMessage = error as { message?: string };
      dispatch(resetVoiceSession());
      ParticipantsStatusEventBus.hearing.$disconnected.next({
        phoneNumber: hearingPhoneNumber,
      });
      if (errorWithMessage?.message?.includes('Invalid dialString')) {
        dispatch(
          handleError({
            error: errorWithMessage,
            methodName: 'createNewVoiceSession',
            title: t('invalidDialString'),
          })
        );
      } else {
        dispatch(
          handleError({
            error: errorWithMessage,
            methodName: 'createNewVoiceSession',
          })
        );
      }
    }
  };

  const handleBackendProcessingDelay = async () => {
    if (isHearingToDeafCall) {
      await createBackendProcessingDelay(
        BACKEND_DISCONNECT_PROCESSING_DELAY_MS
      );
    }
  };

  const initializeConnectionState = () => {
    dispatch(setVoiceSessionLoading(true));
    dispatch(setVoiceSessionStatus(VoiceSessionStatus.CONNECTING));
    if (!isTeaming) {
      ParticipantsStatusEventBus.hearing.$connecting.next(
        primaryHearingPhoneNumber
      );
    }
  };

  const performVoiceConnection = async (
    voiceSessionId: string,
    audioElement: HTMLAudioElement | null
  ) => {
    const voiceMeetingService = VoiceMeetingService.getInstance();

    await connectUserToExistingSession(
      voiceSessionId,
      agentNumber,
      audioElement
    );

    dispatch(
      setVoiceSessionThunk({
        voiceSessionState: {
          sessionId: voiceMeetingService.getSessionId(),
          status: VoiceSessionStatus.ACTIVE,
          isLoading: false,
        },
      })
    );

    voiceMeetingService.setupListeners();
    voiceMeetingService.onAudioVideoDidStart(() => {
      dispatch(hearingAudioReady());
    });
  };

  const handleConnectionError = (error: unknown) => {
    const errorWithMessage = error as { message?: string };
    dispatch(resetVoiceSession());
    ParticipantsStatusEventBus.hearing.$disconnected.next({
      phoneNumber: primaryHearingPhoneNumber,
    });

    const errorMessage = errorWithMessage?.message ?? '';

    if (isAbortError(errorMessage)) {
      // Send as info for abort scenarios (expected behavior)
      dispatch(
        sendAnalyticsInfo({
          methodName: 'connectUserToVoiceSession',
          message: `Voice session connection aborted: ${errorMessage}`,
        })
      );
    } else if (isWarningError(errorMessage)) {
      // Send as warning for completed or not found scenarios
      dispatch(
        sendAnalyticsWarning({
          methodName: 'connectUserToVoiceSession',
          message: `Voice session connection warning: ${errorMessage}`,
        })
      );
    } else {
      // Send as error for real connection failures
      dispatch(
        handleError({
          error: errorWithMessage,
          methodName: 'connectUserToVoiceSession',
          disableUserNotification: true,
        })
      );
    }
  };

  // Main orchestration function
  const connectUserToVoiceSession = async (
    voiceSessionId: string,
    audioElement: HTMLAudioElement | null
  ) => {
    try {
      initializeConnectionState();

      await handleBackendProcessingDelay();

      // Get current session state
      const sessionState = await dispatch(getHearingsSessionState()).unwrap();

      // Validate preconditions
      const validation = validateConnectionPreconditions(sessionState.callId);
      if (!validation.isValid) {
        dispatch(
          sendAnalyticsInfo({
            methodName: 'connectUserToVoiceSession',
            message: `${validation.reason} - will not connect to voice session`,
          })
        );
        throw new Error(validation.reason);
      }

      await performVoiceConnection(voiceSessionId, audioElement);
    } catch (error: unknown) {
      handleConnectionError(error);
    }
  };

  return {
    createNewVoiceSession,
    connectUserToVoiceSession,
    replaceN11Number,
    clearCallErrorIfExists,
  };
};
